<template>
  <loader
    v-if="loading"
    :label="$t('conflict.searchingTodos')"
  />
  <choose-exclusive-and-unique-wigo
    v-else-if="chooseExclusiveAndUniqueWigo"
    :conflict="todo.conflict"
    :showDetails="debugMode"
    @wigoSelected="challengeGroup($event)"
  />
  <v-container
    v-else
  >
    <v-row
      class="white"
      dense
    >
      <v-col
        class="py-0"
      >
        <h3 class="ml-3">{{ $t('conflict.inThisScenario') }}</h3>
        <div class="px-4 py-2">
          <conflict
            v-if="isConflictStarting()"
            :conflict="conflict"
            :showDetails="debugMode"
          />
          <scenario
            v-else
            :scenario="todo.conflict.scenario"
            :showDetails="debugMode"
          />
        </div>
      </v-col>
    </v-row>
    <v-row
      class="my-2"
      dense
    >
      <v-col
        class="px-4 py-2"
      >
        <h3 class="mb-3">{{ $t('option.possible') }}</h3>
        <span
          v-if="availableOptions.length === 0"
          class="caption grey--text"
        >
          {{ $t('option.noMoreOption') }}
        </span>
        <span
          v-else
          v-for="(option, optionIndex) in availableOptions"
          :key="`option-${optionIndex}`"
          :draggable="true"
          @dragstart="dragStartHandler($event, option, -1)"
          @drag="dragHandler($event)"
          class="rai-wigo my-1"
          >
          <option-chip-2
            class="align-self-end"
            :option="option"
            :draggable="true"
            :showDetails="debugMode"
          />
        </span>
      </v-col>
    </v-row>
    <v-row
      class="raison-highlight mt-4"
      dense
    >
        <v-container>
          <v-row>
            <v-col
              class="px-4 py-2"
            >
              <div class="text-h6 pt-3">
                {{ $t('conflict.moveOptionsInBoxes') }}
              </div>
            </v-col>
          </v-row>
          <v-row
            v-for="(group, groupIndex) in groups"
            :key="`group-${groupIndex}`"
            @drop="dropHandler($event, groupIndex)"
            @drop.prevent="highlight($event, -1)"
            @dragleave.prevent="highlight($event, -1)"
            @dragover.prevent="highlight($event, groupIndex)"
            @dragenter.prevent="highlight($event, groupIndex)"
          >
            <v-col
              :class="`${ [groupIndex === dragoverGroupIndex ? 'raison-highlight' : 'white']} elevation-2 mx-2 my-1`"
            >
              <v-tooltip
                v-if="groupIndex > 1"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    @click="removeGroup(groupIndex)"
                    class="float-right"
                    icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon color="grey">
                      mdi-close
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('option.removeGroup') }}</span>
              </v-tooltip>
              <div>
                <span
                  v-if="group.length <= 0"
                  class="caption grey--text"
                >
                  {{ $t('conflict.dragAndDropOptionsHere') }}
                </span>
                <div
                  v-else
                  :class="`${ group.length > 1 ? 'chip-wrap' : 'rai-wigo'} box`"
                >
                  <span
                    v-for="(option, optionIndex) in group"
                    :key="`group-${groupIndex}-option-${optionIndex}`"
                    @dragstart="dragStartHandler($event, option, groupIndex)"
                    @drag="dragHandler($event)"
                    :draggable="true"
                  >
                    <option-chip-2
                      class="align-self-end"
                      :option="option"
                      :draggable="true"
                      :showDetails="debugMode"
                    />
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              class="text-center"
              >
              <v-btn
                class="raison-btn"
                @click="addGroup()"
              >
                <v-icon>mdi-plus</v-icon> {{ $t('conflict.addAGroup')}}
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div class="text-h6 pt-3">
                {{ $t('conflict.howDoYouChooseOneOfTheseGroups') }}
              </div>
            </v-col>
          </v-row>
          <v-row
            justify="center"
            class="choice-row"
          >
            <favorite-option
              shortLabelCode="conflict.chooseDefaultOption"
              answerLabelCode="conflict.iChooseWithoutAdditionalInformation"
              @click="chooseFavoriteGroup()"
              :disabled="!groupCompleted"
            />
            <favorite-option
              shortLabelCode="conflict.addContext"
              answerLabelCode="conflict.iChooseByAddingMoreInformation"
              @click="byAddingInformation()"
              :disabled="!groupCompleted"
            />
            <favorite-option
              shortLabelCode="conflict.finish"
              answerLabelCode="conflict.iHaveFinishedWorkingWithThisScenario"
              @click="finish()"
            />
          </v-row>
        </v-container>
    </v-row>
    <v-row>
      <v-col>
        <contextual-groups
          v-if="todo"
          :groupings="todo.contextualGroups"
          :showDetails="debugMode"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ChooseExclusiveAndUniqueWigo from '@/components/conflictResolution/ChooseExclusiveAndUniqueWigo'
import Conflict from '@/components/conflictResolution/Conflict'
import ContextualGroups from '@/components/conflictResolution/ContextualGroups'
import Loader from '@/components/global/Loader'
import OptionChip2 from '@/components/global/OptionChip2'
import Scenario from '@/components/global/Scenario'
import FavoriteOption from '@/components/conflictResolution/FavoriteOption'

import debugModeMixin from '@/mixins/debug-mode-mixin.js'

import conflictService from '@/services/conflicts.js'
import helpService from '@/services/help.js'

export default {
  name: 'NoIPreferMakingGroups',

  components: {
    ChooseExclusiveAndUniqueWigo,
    Conflict,
    ContextualGroups,
    FavoriteOption,
    Loader,
    OptionChip2,
    Scenario
  },

  mixins: [ debugModeMixin ],

  data () {
    return {
      loading: false,
      availableOptions: [],
      groups: [[],[]],
      dragoverGroupIndex: -1,
      chooseExclusiveAndUniqueWigo: false
    };
  },

  props: {
    conflict: {
      type: Object,
      required: true
    },
    todo: {
      type: Object,
      required: false
    }
  },

  computed: {
    groupCompleted() {
      const result = this.availableOptions.length === 0
        && !this.groups.some(group => group.length === 0)
        && this.groups.some(group => group.length > 1)
      if(result) {
        helpService.setHelpPage(this.$store, 'make_groups_done')
      }
      return result
    }
  },

  methods: {
    highlight(event, groupIndex) {
      event.preventDefault()
      this.dragoverGroupIndex = groupIndex
    },
    isConflictStarting() {
      const value = !this.todo || !this.todo.conflict
      // console.log('isConflictStarting', this.todo, !this.todo, value)
      return value
    },
    addGroup() {
      this.groups.push([])
    },
    removeGroup(groupIndex) {
      this.availableOptions.push(...this.groups[groupIndex])
      this.groups.splice(groupIndex, 1)
    },
    dragStartHandler(event, option, comeFrom) {
      event.dataTransfer.setData('id', option.id) // NB : not setting dataTransfer breaks draggable in firefox
      event.dataTransfer.setData('comeFrom', comeFrom)
    },
    dragHandler (e) {
      if (e.screenY < 150) {
        window.scrollBy(0, -15)
      } else if ((window.outerHeight - e.screenY) < 80) {
        window.scrollBy(0, 15)
      }
    },
    dropHandler (event, groupIndex) {
      event.preventDefault()
      this.dragoverGroupIndex = -1
      const optionId = event.dataTransfer.getData('id')
      const comeFrom = event.dataTransfer.getData('comeFrom')
      let group
      if(comeFrom == -1) {
        group = this.availableOptions
      }
      else {
        group = this.groups[comeFrom]
      }

      const optionIndex = group.findIndex(opt => opt.id === optionId)
      const option = group[optionIndex]
      group.splice(optionIndex, 1)
      this.groups[groupIndex].push(option)
    },
    chooseFavoriteGroup() {
      this.todo.conflict.wigos = this.groups
      this.chooseExclusiveAndUniqueWigo = true
      helpService.setHelpPage(this.$store, 'choose_exclusive_and_unique_wigo')
    },
    challengeGroup(wigo) {
      const conflictId = this.$route.params.conflict
      console.log('challenge group', conflictId)
      this.loading = true
      const projectId = this.$route.params.id
      const scenarios = this.todo ? this.todo.conflict.combinedScenarios : this.conflict.combinedScenarios
      conflictService.prefer(projectId, conflictId, {
        resolution: "ChooseExclusiveAndUniqueWigoWithGroups",
        wigo: wigo.map(option => option.id),
        logos: this.groups.filter(group => group != wigo).map(options => options.map(option => option.id)),
        scenarios: scenarios,
      })
      .then(response => {
        const todo = response.data
        this.$emit('nextTodo', todo)
      })
      .catch(err => {
        this.$emit('raison-event', { type:'error', messages:[err] })
      })
      .finally(() => {
        this.editing = false
        this.loading = false
      });
    },
    byAddingInformation() {
      console.log('prefer groups by adding information')
      const askElementsToAddTodo = JSON.parse(JSON.stringify(this.todo))
      // delete askElementsToAddTodo.id
      askElementsToAddTodo.function = "choose_by_adding_information"
      askElementsToAddTodo.conflict.wigos = [this.groups[0]]
      askElementsToAddTodo.conflict.logos = this.groups.slice(1)
      console.log('prefer groups by adding information', askElementsToAddTodo)
      this.$emit('nextTodo', askElementsToAddTodo)
    },
    finish() {
      console.log('finish', this.conflict)
      this.loading = true
      const projectId = this.$route.params.id
      const conflictId = this.$route.params.conflict
      conflictService.prefer(projectId, conflictId, {
        resolution: "Solved",
        todoId: this.todo?.id
      })
      .then(response => {
        const todo = response.data
        this.$emit('nextTodo', todo)
      })
      .catch(err => {
        this.$emit('raison-event', { type:'error', messages:[err] });
      })
      .finally(() => {
        this.loading = false;
      });
    }
  },

  created() {
    // this.loading = true
    if(this.todo) {
      this.availableOptions = [...this.todo.conflict.wigos.flat()]
    }
    else {
      this.availableOptions = [...this.conflict.wigos.flat()]
    }
  }
};
</script>

<style scoped>
.rai-select-option-card {
  cursor: pointer;
}
.choice-row {
  display: block;
}
</style>
